/**
 * The 44px column at the end of a ticket row.
 *
 * Status is here rather than only on the detail page because moving a ticket
 * along is the one thing an operator does from the queue itself - reading it is
 * what the detail page is for. Copying the number matters more than it looks:
 * it is what a ticket is called in an e-mail, in a call, and in the client's own
 * message about it.
 */
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Ticket, TicketStatus } from "../../../lib/support";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";
import { STATUSES } from "./tone";

export function TicketRowMenu({
  ticket,
  onStatus,
  onDelete,
}: {
  ticket: Ticket;
  onStatus: (s: TicketStatus) => void;
  onDelete: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  async function copyNumber() {
    close();
    try {
      await navigator.clipboard.writeText(ticket.number);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu never gets the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button type="button" className="crm-mi" onClick={() => void copyNumber()}>
          <Copy className="size-4" />
          {t("support.copyNumber")}
        </button>

        <div className="crm-menu-sep" />
        <div className="crm-menu-head">
          <div className="crm-menu-t">{t("support.moveTo")}</div>
        </div>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={cn("crm-mi", s === ticket.status && "is-on")}
            onClick={() => {
              close();
              if (s !== ticket.status) onStatus(s);
            }}
          >
            {t(`support.statusLabel.${s}`)}
          </button>
        ))}

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}
